import React from 'react';
import styled from 'styled-components';
import { Body5, Container, H5, Page } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { AI_TOOLS, DOC_SELECT_MODES, FOLLOW_UP_QUESTION_IDENTIFIER } from '@constants';
import { sleep } from '@utils/usefulFuncs';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { QuestionResult } from './components/QuestionResult';
import { SelectedDocuments } from './components/SelectedDocuments';
import { FormState } from '@ui/FormState';
import InfoIconTooltip from '@ui/InfoIconTooltip';
import { InfoIconTypes } from '@constants/infoIcons';
import { AdvancedSettingsButton } from './AdvancedSettings';
import { useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { useAskDigitalAnalyst } from '@hooks/features/useAskDigitalAnalyst';
import { TickerDocGenerator } from './components/TickerSelect';
import { logError, logNetRequest, filterDuplicateDocuments, includesId } from '@utils/usefulFuncs';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
import { useStreamingResult } from '@hooks/features/useStreamingResult';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { PrefillQuestionModal } from './components/PrefillQuestionModal';
import { StandaloneDocSelectModal } from '@containers/docSelection/StandaloneDocSelectModal';
import { sendApiRequest } from '@services/api';

const QuestionAnswerWrapper = styled.div`
  overflow: auto;
  padding-bottom: 24px;
`;

const SubmitButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;
const Label = styled.div`
  padding-bottom: 8px;
`;
const FirstActionsRowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 0 16px 0;
`;
const ActionsWrapper = styled.div`
  @media (max-width: 440px) {
    button {
      padding: 12px 12px !important;
    }
  }
`;
const Heading = styled.div`
  padding: 36px 0 0 0;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  h5 {
    padding-right: 8px;
  }
`;
const SourcesHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  padding-bottom: 16px;
`;

function selectDocSelectModes({ isQueryMode, isCustomDocsMode, isFedMode, isWebMode, isFedGpt }) {
  if (isCustomDocsMode) {
    return [DOC_SELECT_MODES.client];
  }
  if (isQueryMode) {
    return [DOC_SELECT_MODES.allCompany, DOC_SELECT_MODES.myDocs]; // DOC_SELECT_MODES.docName,
  }
  if (isFedMode || isFedGpt) {
    return [DOC_SELECT_MODES.docType, DOC_SELECT_MODES.speaker, DOC_SELECT_MODES.myDocs];
  }
  return [DOC_SELECT_MODES.docType, DOC_SELECT_MODES.myDocs];
}

const selectDefaultDocSelectMode = ({ isCustomDocsMode, isQueryMode, isFedMode }) => {
  if (isCustomDocsMode) {
    return DOC_SELECT_MODES.client;
  }
  if (isQueryMode) {
    return DOC_SELECT_MODES.allCompany;
  }
  if (isFedMode) {
    return DOC_SELECT_MODES.docType;
  }
  return DOC_SELECT_MODES.docType;
};

const SourcesHeading = () => {
  return (
    <SourcesHeadingWrapper>
      <Container right={6} bottom={2}>
        <Body5>Sources</Body5>
      </Container>
      <InfoIconTooltip type={InfoIconTypes.SOURCES} />
    </SourcesHeadingWrapper>
  );
};

const AddCitationsButton = ({ resultId, queryRunId, model, handleAskNewQuestion }) => {
  const [queueCitations, setQueueCitations] = React.useState(false);
  const [requestedCitations, setRequestedCitations] = React.useState(false);

  React.useEffect(() => {
    setRequestedCitations(false);
    setQueueCitations(false);
  }, [resultId]);

  const hc = () => {
    setRequestedCitations(true);

    const forceFollowUpQuestionOff = true;
    const isCitationsMode = true;
    handleAskNewQuestion('question in api', model, forceFollowUpQuestionOff, null, null, isCitationsMode, queryRunId);
  };

  if (requestedCitations) {
    return <TransparentFlatButton onClick={hc}>Re-run Citations</TransparentFlatButton>;
  }

  if (queueCitations) {
    return (
      <FlatButton disabled onClick={() => null}>
        Queued Citations
      </FlatButton>
    );
  }

  return (
    <FlatButton onClick={hc} disabled={!queryRunId}>
      Add Citations
    </FlatButton>
  );
};

const SearchForMoreDetailButton = ({ resultId, question, model, handleAskNewQuestion }) => {
  const { result: answer } = useStreamingResult(resultId);

  const handleSearchMoreDetail = () => {
    const newQuestion = `Improve this answer by including more details, previous question was: "${question}"; previous answer was: "${
      answer.split(FOLLOW_UP_QUESTION_IDENTIFIER)[0] || ''
    }"`;
    const forceFollowUpQuestionOff = true;
    const forceQuestionAsked = `Improve the answer to the question: ${question}`;
    handleAskNewQuestion(newQuestion, model, forceFollowUpQuestionOff, forceQuestionAsked);
  };

  return <FlatButton onClick={handleSearchMoreDetail}>Search for more detail</FlatButton>;
};

const ActionsComponent = ({ resultId, queryRunId, question, model, handleReviseQuestion, handleAskNewQuestion }) => {
  const toolsAvailable = useCurrentUserToolsAvailable();
  const isCitationsEnabled = toolsAvailable.includes(AI_TOOLS.CITATIONS);

  return (
    <ActionsWrapper>
      <FirstActionsRowWrapper>
        <SearchForMoreDetailButton
          resultId={resultId}
          question={question}
          model={model}
          handleAskNewQuestion={handleAskNewQuestion}
        />

        {isCitationsEnabled && (
          <AddCitationsButton
            resultId={resultId}
            queryRunId={queryRunId}
            question={question}
            model={model}
            handleAskNewQuestion={handleAskNewQuestion}
          />
        )}
      </FirstActionsRowWrapper>
      <Container>
        <SkeletonButton onClick={(e) => handleReviseQuestion()}>Revise Question</SkeletonButton>
      </Container>
    </ActionsWrapper>
  );
};

export default function NewQueryTool(props) {
  const {
    preSelectedDocs,
    preSelectedTickers,
    onGoBack,
    isResearchMode,
    isQueryMode,
    isFedMode,
    isWebMode,
    isFedGpt,
    isCustomDocsMode,
    tool,
    isRFPMode,
    isEvestmentMode,
    heading,
    docModes: overrideDocModes,
    defaultActiveDocSelectMode: overrideDefaultActiveDocSelectMode,
  } = props;

  const toolsAvailable = useCurrentUserToolsAvailable();
  const isCompanyTaggingEnabled = toolsAvailable.includes(AI_TOOLS.COMPANY_TAGGING);

  const tickersRef = React.useRef(preSelectedTickers || []);

  const [docSelectModes] = React.useState(selectDocSelectModes(props));
  const [defaultActiveDocSelectMode] = React.useState(selectDefaultDocSelectMode(props));

  const [isSelectingPrefillQuestion, setIsSelectingPrefillQuestion] = React.useState(false);
  const [isSelectingDocs, setIsSelectingDocs] = React.useState(false);
  const [selectedDocs, setSelectedDocs] = React.useState(preSelectedDocs || []);
  const [question, setQuestion] = React.useState('');
  const { generate_follow_ups } = useAdvancedSettings();
  const showViewDocsMessage = isFedMode;

  const autoSelectModeDefaultDocs = async () => {
    if (isRFPMode) {
      const URL = 'v1/genai/documents/default?user_mode=' + tool;
      const { status, data } = await sendApiRequest('get', URL);
      logNetRequest(status, data, tool);

      if (status === 200) {
        const autoSelectedDocs = data.documents;
        setSelectedDocs((prev) => filterDuplicateDocuments([...prev, ...autoSelectedDocs]));
      }
    }
  };

  React.useEffect(() => {
    autoSelectModeDefaultDocs();
  }, []);

  const {
    prevUsedModel,
    onSubmit,
    onClearResult,

    resultId,
    citationsResultId,
    sourceDocs,

    questionAsked,
    formError,
    isSubmittingToApi,
  } = useAskDigitalAnalyst(tool);

  const onDismiss = () => setIsSelectingDocs(false);

  const onBackClick = () => {
    if (resultId === null) {
      onGoBack();
    } else {
      onClearAnswer();
    }

    autoSelectModeDefaultDocs();
  };

  const onClearAnswer = () => {
    onClearResult();
    onReset();
  };

  const onReset = () => {
    onClearResult();
    setSelectedDocs([]);
    setQuestion('');
  };

  const onSelectedDocsChange = (t) => setSelectedDocs(t);

  const onQuestionChange = (e) => {
    const {
      target: { value },
    } = e;
    setQuestion(value);
  };

  const onSelectDocumentsClick = () => setIsSelectingDocs(true);

  const handleAddDocument = (v) => {
    if (Array.isArray(v)) {
      onSelectedDocsChange((prevState) => filterDuplicateDocuments([...prevState, ...v]));
    } else {
      onSelectedDocsChange((prevState) => filterDuplicateDocuments([...prevState, v]));
    }
  };

  const handleRemoveDocument = (filenameOrFilenames) => {
    if (Array.isArray(filenameOrFilenames)) {
      onSelectedDocsChange((prevState) => prevState.filter((d) => !includesId(filenameOrFilenames, d.id)));
    } else {
      onSelectedDocsChange((prevState) => prevState.filter((d) => d.id !== filenameOrFilenames.id));
    }
  };

  const handleRemoveTickerDocuments = (company) => {
    onSelectedDocsChange((prevState) =>
      prevState.filter((d) =>
        company.doc_tag_id ? d.doc_tag_id === company.doc_tag_id : d.security_symbol !== company.symbol
      )
    );
  };

  const handleReviseQuestion = () => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      try {
        const textarea = document.querySelector('textarea[name="question"]');
        textarea?.focus();
      } catch (e) {
        logError(e);
      }
    }, 100);
  };

  const handleAskNewQuestion = async (
    question,
    model,
    forceFollowUpQuestionOff,
    forceQuestionAsked,
    forceContext,
    isCitationsMode,
    resultId
  ) => {
    if (question) {
      const isAPICitationsMode = isCitationsMode;

      await sleep(100);
      if (!isAPICitationsMode) {
        setQuestion(forceQuestionAsked ? forceQuestionAsked : question);
      }
      const form = {
        question,
        model,
        forceFollowUpQuestionOff,
        forceQuestionAsked,
        selectedDocs: forceContext ? [] : selectedDocs,
        isCitationsMode,
        isAPICitationsMode,
        isRFPMode,
      };
      if (isAPICitationsMode) {
        form.record_id = resultId;
      }
      if (forceContext) {
        form.extra_context = forceContext;
      }
      onSubmit(form);
    } else {
      handleReviseQuestion();
      setQuestion('');
    }
  };

  const handleSelectPrefillQuestion = (v) => setQuestion(v);

  return (
    <Container>
      {isSelectingDocs ? (
        <StandaloneDocSelectModal
          docSelectModes={overrideDocModes || docSelectModes}
          defaultActiveMode={overrideDefaultActiveDocSelectMode || defaultActiveDocSelectMode}
          onDismiss={onDismiss}
          tickersRef={tickersRef}
          selectedDocs={selectedDocs}
          handleAddDocument={handleAddDocument}
          handleRemoveDocument={handleRemoveDocument}
          tool={tool}
        />
      ) : null}
      {isSelectingPrefillQuestion && (
        <PrefillQuestionModal
          onSelect={handleSelectPrefillQuestion}
          onDismiss={() => setIsSelectingPrefillQuestion(false)}
        />
      )}
      <Page width={'1000px'}>
        <BackArrowButton onClick={onBackClick} />
        <Heading>
          <H5>{heading ? heading : isFedGpt ? 'Ask FedGPT' : 'Ask Digital Analyst'}</H5>
          {heading ? null : <InfoIconTooltip type={isFedGpt ? InfoIconTypes.FED_GPT : InfoIconTypes.QUERY_TOOL} />}
          <AdvancedSettingsButton tool={tool} />
        </Heading>
        <FormWrapper>
          <Container top={36}>
            {isQueryMode ? (
              <Container bottom={16}>
                <TickerDocGenerator
                  tickersRef={tickersRef}
                  preSelectedTickers={preSelectedTickers}
                  onSelectDocs={handleAddDocument}
                  onRemoveTicker={handleRemoveTickerDocuments}
                  isQueryMode={isQueryMode}
                  isFedMode={isFedMode}
                  isWebMode={isWebMode}
                  isFedGpt={isFedGpt}
                  isCustomDocsMode={isCustomDocsMode}
                  showCompanyTags={isCompanyTaggingEnabled}
                />
              </Container>
            ) : null}
          </Container>
          <Container row verticallyCenter spaceBetween top={8}>
            <Label>
              <Body5>Question</Body5>
            </Label>
            {isQueryMode ? (
              <Container bottom={8}>
                <TransparentFlatButton onClick={() => setIsSelectingPrefillQuestion(true)}>
                  Use Pre-filled Questions
                </TransparentFlatButton>
              </Container>
            ) : null}
          </Container>
          <Container>
            <textarea name={'question'} onChange={onQuestionChange} value={question} />
          </Container>

          {showViewDocsMessage && (
            <Container top={16}>
              <Body5>Click any document name below to open the source file</Body5>
            </Container>
          )}
          <Container top={16}>
            <SourcesHeading />
            <SelectedDocuments
              onSelectDocumentsClick={onSelectDocumentsClick}
              selectedDocs={selectedDocs}
              onSelectedDocsChange={onSelectedDocsChange}
              showAddAllDocumentsAction={false}
            />
          </Container>
          <Container top={48}>
            <Container centerAll>
              <FormState error={formError} />
            </Container>
            <SubmitButtonWrapper>
              <FlatButton
                onClick={() => onSubmit({ question, selectedDocs, isResearchMode, isRFPMode, isEvestmentMode })}
                disabled={isSubmittingToApi}
                fullWidth
              >
                {isSubmittingToApi ? 'Submitting...' : 'Submit'}
              </FlatButton>
            </SubmitButtonWrapper>
            <Container top={8} centerAll>
              <TransparentFlatButton onClick={onReset} fullWidth>
                Reset
              </TransparentFlatButton>
            </Container>
          </Container>
        </FormWrapper>
      </Page>
      <div id={'question-answer'}></div>
      {resultId || isSubmittingToApi ? (
        <Page width={'1000px'}>
          <QuestionAnswerWrapper>
            <QuestionResult
              isSubmittingToApi={isSubmittingToApi}
              resultId={resultId}
              citationsResultId={citationsResultId}
              model={prevUsedModel}
              userMode={tool}
              question={questionAsked}
              sourceDocs={sourceDocs}
              selectedDocs={selectedDocs.map((d) => d.id)}
              handleAskQuestion={handleAskNewQuestion}
              enableMoreDetail
              enableQualityCheck
              enableFollowUpQuestions={generate_follow_ups}
              isRFPMode={isRFPMode}
              ActionsComponent={({ queryRunId }) => (
                <Container top={16}>
                  <ActionsComponent
                    resultId={resultId}
                    queryRunId={queryRunId}
                    question={questionAsked}
                    handleReviseQuestion={handleReviseQuestion}
                    handleAskNewQuestion={handleAskNewQuestion}
                  />
                </Container>
              )}
            />
          </QuestionAnswerWrapper>
        </Page>
      ) : null}
    </Container>
  );
}
